import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useSessionStore, selectQuota } from '../state/sessionStore';

interface QuotaBannerProps {
  className?: string;
}

export function QuotaBanner({ className }: QuotaBannerProps) {
  const { t } = useTranslation();
  const quota = useSessionStore(selectQuota);

  if (!quota) {
    return null;
  }

  const percentage = quota.total > 0 ? Math.round((quota.remaining / quota.total) * 100) : 0;
  const isBlocked = quota.status === 'blocked' || quota.remaining <= 0;
  const isLimited = quota.status === 'limited';

  const statusLabel = isBlocked
    ? t('memberPage.quota.statusBlocked')
    : isLimited
    ? t('memberPage.quota.statusLimited')
    : t('memberPage.quota.statusActive');

  return (
    <div
      className={clsx(
        'flex flex-col gap-2 rounded-2xl border px-5 py-4 text-sm shadow-ai-card sm:flex-row sm:items-center sm:justify-between',
        isBlocked
          ? 'border-ai-danger/50 bg-ai-surface-elevated/70 text-ai-danger'
          : isLimited
          ? 'border-ai-warning/40 bg-ai-surface-elevated/70 text-ai-warning'
          : 'border-ai-primary/30 bg-ai-surface-elevated/70 text-ai-text-secondary',
        className
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col gap-1">
        <span className="text-xs uppercase tracking-[0.3em] text-ai-text-secondary/80">
          {t('memberPage.quota.heading')}
        </span>
        <strong className="text-lg text-ai-text-primary">
          {t('memberPage.quota.remaining', { remaining: quota.remaining })}
        </strong>
        <span>{statusLabel}</span>
      </div>
      <div className="flex flex-col items-start gap-1 sm:items-end">
        <span className="text-xs text-ai-text-secondary/80">
          {t('memberPage.quota.percentage', { percentage })}
        </span>
        <div className="h-2 w-44 overflow-hidden rounded-full bg-ai-surface-glow/20">
          <div
            className={clsx(
              'h-full transition-all',
              isBlocked ? 'bg-ai-danger/80' : isLimited ? 'bg-ai-warning/80' : 'bg-ai-primary/80'
            )}
            style={{ width: `${Math.min(Math.max(percentage, 0), 100)}%` }}
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}
